import { useContext, useMemo } from "react"
import { GetApp } from "../../App"
import { Localization } from "../../App/Localization"
import { LocalizedMessage } from "../../Components/LocalizedMessage"
import { NavigationLightsIcon } from "../../Components/NavigationLightsIcon"
import { IAlertAnchorDriftData, IAlertDepthData, IAlertLightsData, IAlertLostPositionData, IAlertManOverboardData, IAlertMessageData, IAlertStatus, IAlertStatusType, ILogLighting, IShipVesselType, IStatus } from "../../Types"
import { ShipContext } from "../../App/Context"
import { lightingTranslationKey } from "../../Components/LogButton/Lighting"

export function alertProblemSummary(app: ReturnType<typeof GetApp>, alert: IStatus, vesselType?: IShipVesselType): string | undefined {
  try {
    const data = JSON.parse(alert.data || "{}");
    switch (alert.name) {
      case IAlertStatusType.anchorDrift:
        // The drift distance is meters, like every other anchor-watch radius (#1809).
        return app.format("meters", (data as IAlertAnchorDriftData).distance);
      case IAlertStatusType.depth:
        return app.format("depth", (data as IAlertDepthData).depth);
      case IAlertStatusType.lights: {
        const lights = data as IAlertLightsData;
        const recorded = app.t(lightingTranslationKey(lights.recorded ?? ILogLighting.none, vesselType));
        const recommended = app.t(lightingTranslationKey(lights.recommended ?? ILogLighting.none, vesselType));
        return `${recorded} → ${recommended}`;
      }
      case IAlertStatusType.lostPosition:
        return app.t(`LogView_log_alert_lostPosition_${(data as IAlertLostPositionData).reason}`);
      case IAlertStatusType.message:
        return (data as IAlertMessageData).message;
      case IAlertStatusType.manOverboard:
        return app.t(`LogView_log_alert_manOverboard_${(data as IAlertManOverboardData).status}`);
      default:
        return undefined;
    }
  } catch {
    return undefined;
  }
}

// The alert-type-specific detail text (with any localized substitution values, e.g.
// distance/depth) for a given alert Status. Shared between the full alert dialog
// (AlertView) and the anchor-monitor panel's inline active-alert row, so the same
// detail wording/format is shown wherever an alert can be acted on.
export function AlertMessage({ alert, lightingIcons, vesselType: suppliedVesselType }: { alert: IStatus; lightingIcons?: boolean; vesselType?: IShipVesselType }) {
  const app = GetApp();
  const shipContext = useContext(ShipContext);
  const vesselType = suppliedVesselType ?? shipContext?.ship?.vesselType;

  function Status(name: string, value: number) {
    return [name, value].join("-");
  }

  return useMemo(() => {
    const data = JSON.parse(alert.data || "{}");
    const anchorDrift = data as IAlertAnchorDriftData;
    const depth = data as IAlertDepthData;
    const lostPosition = data as IAlertLostPositionData;
    const lights = data as IAlertLightsData;
    const message = data as IAlertMessageData;
    const manOverboard = data as IAlertManOverboardData;
    if (!alert.name) {
      return null;
    }
    switch (Status(alert.name, alert.value)) {
      case Status(IAlertStatusType.message, IAlertStatus.alarm):
      case Status(IAlertStatusType.message, IAlertStatus.warning):
        return <span className="AlertMessage">{message.message}</span>;
      case Status(IAlertStatusType.anchorDrift, IAlertStatus.alarm):
        return <LocalizedMessage message={(m) => m.alert.anchorDrift.alarm} replace={{ distance: app.format("meters", anchorDrift.distance) }} />;
      case Status(IAlertStatusType.anchorDrift, IAlertStatus.warning):
        return <LocalizedMessage message={(m) => m.alert.anchorDrift.warning} replace={{ distance: app.format("meters", anchorDrift.distance) }} />;
      case Status(IAlertStatusType.depth, IAlertStatus.alarm):
        return <LocalizedMessage message={(m) => m.alert.depth.alarm} replace={{ depth: app.format("depth", depth.depth) }} />;
      case Status(IAlertStatusType.depth, IAlertStatus.warning):
        return <LocalizedMessage message={(m) => m.alert.depth.warning} replace={{ depth: app.format("depth", depth.depth) }} />;
      case Status(IAlertStatusType.lights, IAlertStatus.warning): {
        if (lightingIcons) {
          const values = {
            recommended: lights.recommended ?? ILogLighting.none,
            recorded: lights.recorded ?? ILogLighting.none,
          };
          const parts = (Localization.Messages[app.language]?.alert?.lights?.warning ?? "")
            .split(/(\*\*\{(?:recommended|recorded)\}\*\*)/);
          return (
            <div className="LocalizedMessage AlertMessage-lights">
              <p>
                {parts.map((part, index) => {
                  const name = part.includes("{recommended}") ? "recommended" : part.includes("{recorded}") ? "recorded" : undefined;
                  if (!name) {
                    return part;
                  }
                  const lighting = values[name];
                  const title = app.t(lightingTranslationKey(lighting, vesselType));
                  return (
                    <strong className={`AlertMessage-lights-value ${name}`} key={index}>
                      <NavigationLightsIcon lighting={lighting} title={title} />
                      <span>{title}</span>
                    </strong>
                  );
                })}
              </p>
            </div>
          );
        }
        return <LocalizedMessage message={(m) => m.alert.lights.warning} replace={{ recommended: app.t(lightingTranslationKey(lights.recommended ?? ILogLighting.none, vesselType)), recorded: app.t(lightingTranslationKey(lights.recorded ?? ILogLighting.none, vesselType)) }} />;
      }
      case Status(IAlertStatusType.lostPosition, IAlertStatus.alarm):
        return <LocalizedMessage message={(m) => m.alert.lostPosition[lostPosition.reason]} />;
      case Status(IAlertStatusType.lostPosition, IAlertStatus.warning):
        return <LocalizedMessage message={(m) => m.alert.lostPosition[lostPosition.reason]} />;
      // MOB never opens as a `.warning` — every trigger (device or manual) opens directly
      // at `.alarm` (see MOBConsumer / AppNavigation.triggerManOverboard).
      case Status(IAlertStatusType.manOverboard, IAlertStatus.alarm):
        return <LocalizedMessage message={(m) => m.alert.manOverboard[manOverboard.status]} />;
      default:
        return <span>Unknown {alert.name}</span>;
    }
  }, [alert, app, app.language, lightingIcons, vesselType]);
}
